"use client"
import * as React from "react"
import classNames from "classnames"
import { GoArrowUpRight, GoArrowDownRight } from "react-icons/go"

import {
  checkAddressWithPlaces,
  requestGoogleMaps,
  useReplayAddressOnMapsLoad,
} from "../../app/hooks/use-google-maps"
import {
  getAddressProperty,
  getCityName,
  getLocationData,
  extractCity,
  geocodePlaceByPlaceId,
} from "../../lib/google-maps/geocode"
import type { GoogleAddressData } from "../../lib/google-maps/geocode"

export type { GoogleAddressData } from "../../lib/google-maps/geocode"

type Prediction = {
  placeId: string
  description: string
}

type PlaceInputProps = {
  value: GoogleAddressData | null
  onChange: (value: GoogleAddressData | null) => void
  onBlur?: () => void
  onFocus?: () => void
  mapsLoaded: boolean
  warning?: string
  error?: string
  disabled?: boolean
  placeholder?: string
  searchOptions?: {
    types: string[]
    componentRestrictions: {
      country: string
    }
  }
  addressFormatter?: (data: GoogleAddressData) => string
  className?: string
  clearOnInputChange?: boolean
  allowCityPrediction?: boolean
  testId?: string
  type?: "pickup" | "dropoff"
  id?: string
  name?: string
}

const defaultSearchOptions = {
  types: [] as string[],
  componentRestrictions: { country: "us" },
}

export const PlaceInput = /* @__PURE__ */ React.forwardRef<
  HTMLInputElement,
  PlaceInputProps
>(function PlaceInput(props: PlaceInputProps, ref) {
  const {
    mapsLoaded,
    allowCityPrediction = false,
    searchOptions = defaultSearchOptions,
  } = props

  const [address, setAddress] = React.useState("")
  const [predictions, setPredictions] = React.useState<Prediction[]>([])
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const [checkOnBlur, setCheckOnBlur] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)

  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const autocompleteServiceRef = React.useRef<google.maps.places.AutocompleteService | null>(
    null,
  )
  const predictionsRef = React.useRef<Prediction[]>([])
  // The text the held suggestions were fetched for.
  const predictionsInputRef = React.useRef("")
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  predictionsRef.current = predictions

  React.useEffect(() => {
    if (props.value && props.value.formatted_address) {
      setAddress(props.value.formatted_address)
    } else {
      setAddress("")
    }
  }, [props.value])

  React.useEffect(() => {
    if (mapsLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService()
    }
  }, [mapsLoaded])

  const fetchPredictions = React.useCallback(
    (input: string) => {
      if (!autocompleteServiceRef.current || !input.trim()) {
        setPredictions([])
        setIsOpen(false)

        return
      }

      autocompleteServiceRef.current.getPlacePredictions(
        {
          input,
          types: searchOptions.types,
          componentRestrictions: searchOptions.componentRestrictions,
        },
        (results, status) => {
          // The visitor left before Places answered: no list under a left field.
          if (document.activeElement !== inputRef.current) {
            return
          }

          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            const mapped = results.map((r) => ({
              placeId: r.place_id,
              description: r.description,
            }))

            predictionsInputRef.current = input
            setPredictions(mapped)
            setActiveIndex(0)
            setIsOpen(true)
          } else {
            setPredictions([])
            setIsOpen(false)
          }
        },
      )
    },
    [searchOptions],
  )

  const addressCheck = {
    request: searchOptions,
    onMatch: (description: string, placeId: string) =>
      selectPrediction(description, placeId),
    onNoMatch: () => {
      props.onChange(null)
      setAddress("")
    },
  }

  const pendingBlurValidationRef = useReplayAddressOnMapsLoad(
    mapsLoaded,
    inputRef,
    { suggest: fetchPredictions, validate: addressCheck },
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    setAddress(value)

    if (!checkOnBlur) {
      setCheckOnBlur(true)
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    if (mapsLoaded) {
      debounceTimerRef.current = setTimeout(() => {
        fetchPredictions(value)
      }, 300)
    }
  }

  const selectPrediction = async (description: string, placeId: string) => {
    if (
      predictionsRef.current.length > 0 &&
      !predictionsRef.current.some(
        (p) => p.description.toLowerCase() === description.toLowerCase(),
      )
    ) {
      return
    }

    try {
      const { coordinates } = await geocodePlaceByPlaceId(placeId)
      const geocoder = new google.maps.Geocoder()
      const placeIdResults = await new Promise<google.maps.GeocoderResult[]>(
        (resolve, reject) => {
          geocoder.geocode({ placeId }, (results, status) => {
            if (status === "OK" && results) {
              resolve(results)
            } else {
              reject(status)
            }
          })
        },
      )

      const locationData = await getLocationData(coordinates, placeIdResults)

      const cityName = getCityName(locationData.address_components)
      const stateShortName = getAddressProperty(
        locationData.address_components,
        "administrative_area_level_1",
        "short_name",
      )
      const zipCode = getAddressProperty(
        locationData.address_components,
        "postal_code",
        "long_name",
      )

      const routeData: GoogleAddressData = {
        formatted_address: description,
        city: allowCityPrediction
          ? extractCity(cityName, description)
          : cityName || "",
        state_code: stateShortName || "",
        lat: coordinates.lat,
        lng: coordinates.lng,
        zip: zipCode,
      }

      if (props.addressFormatter) {
        routeData.formatted_address = props.addressFormatter(routeData)
      }

      if (props.clearOnInputChange) {
        setAddress("")
      } else {
        setAddress(routeData.formatted_address)
      }

      props.onChange(routeData)
      setCheckOnBlur(false)
      setPredictions([])
      setIsOpen(false)
    } catch {
      props.onChange(null)
    }
  }

  const handleBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    setTimeout(() => {
      const isWaitingForPlaces =
        !autocompleteServiceRef.current && address.trim() !== ""

      if (checkOnBlur && isWaitingForPlaces) {
        pendingBlurValidationRef.current = true
      } else if (checkOnBlur) {
        const currentPredictions = predictionsRef.current
        const matched = currentPredictions.find(
          (p) => p.description.toLowerCase() === address.trim().toLowerCase(),
        )
        // A paste left within the debounce has no suggestions for its text yet.
        const holdsSuggestionsForText =
          currentPredictions.length > 0 &&
          predictionsInputRef.current.trim() === address.trim()
        const needsPlacesLookup =
          !holdsSuggestionsForText && address.trim() !== ""

        if (matched) {
          selectPrediction(matched.description, matched.placeId)
        } else if (needsPlacesLookup) {
          // selectPrediction refuses places missing from a held list, and the
          // list held now is for other text.
          predictionsRef.current = []
          checkAddressWithPlaces(address, inputRef, addressCheck)
        } else {
          props.onChange(null)
          setAddress("")
        }
      }

      setPredictions([])
      setIsOpen(false)
      props.onBlur?.()
    }, 200)
  }

  const handleFocus = () => {
    requestGoogleMaps()
    // Back in the field: the next blur runs its own check.
    pendingBlurValidationRef.current = false

    if (!checkOnBlur && !props.value) {
      setCheckOnBlur(true)
    }

    props.onFocus?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || predictions.length === 0) {
      return
    }

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()

      if (activeIndex >= 0 && activeIndex < predictions.length) {
        const prediction = predictions[activeIndex]

        setAddress(prediction.description)
        selectPrediction(prediction.description, prediction.placeId)
      }
    } else if (e.key === "Escape") {
      setPredictions([])
      setIsOpen(false)
    }
  }

  const handleSuggestionMouseDown = (prediction: Prediction) => {
    setAddress(prediction.description)
    selectPrediction(prediction.description, prediction.placeId)
  }

  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node

      if (typeof ref === "function") {
        ref(node)
      } else if (ref) {
        ;(ref as React.MutableRefObject<HTMLInputElement | null>).current = node
      }
    },
    [ref],
  )

  return (
    <div className={classNames("space-y-2 relative")}>
      <div className={classNames("relative")}>
        <span className="absolute z-10 left-4 top-1/2 transform -translate-y-1/2">
          {props.type === "pickup" ? (
            <GoArrowUpRight size={24} className="text-neutral-600" />
          ) : (
            <GoArrowDownRight size={24} className="text-neutral-600" />
          )}
        </span>
        <input
          className={classNames(
            "p-4 rounded pl-12 w-full relative flex items-center space-x-2 text-left text-base focus:outline-none",
            "border peer",
            props.error != null
              ? "border-red-500 text-red-500"
              : "border-gray51 text-neutral-600 focus:border-gray51",
          )}
          ref={setRefs}
          value={address}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onPointerDown={requestGoogleMaps}
          onKeyDown={handleKeyDown}
          disabled={props.disabled}
          id={props.id}
          data-testid={props.testId}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${props.name}-listbox`}
          aria-autocomplete="list"
        />
        <label
          className={classNames(
            "absolute left-12 top-1/2 transform -translate-y-1/2 transition peer-focus:-translate-y-7 peer-focus:text-sm",
            props.error != null && "text-red-500",
            props.value !== null && "-translate-y-7",
          )}
          htmlFor={props.id}
        >
          {props.placeholder}
        </label>
        <ul
          id={`${props.name}-listbox`}
          role="listbox"
          className={classNames("pb-0 mb-0", {
            "absolute left-0 top-full w-full border border-neutral-700 bg-white z-20 px-0":
              predictions.length > 0,
          })}
        >
          {predictions.map((prediction, index) => (
            <li
              className={classNames(
                "px-4 py-2.5 text-base cursor-pointer",
                index === activeIndex && "bg-primary-500 text-white",
              )}
              key={prediction.placeId}
              onMouseDown={() => handleSuggestionMouseDown(prediction)}
              onMouseEnter={() => setActiveIndex(index)}
              role="option"
              aria-selected={index === activeIndex}
            >
              <span>{prediction.description}</span>
            </li>
          ))}
          {predictions.length > 0 && (
            <li className={classNames("px-4 py-2 text-right")}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
            </li>
          )}
        </ul>
      </div>
      {props.warning && !props.error && (
        <p
          className={classNames(
            "inline-block text-base leading-none text-secondary-500",
          )}
        >
          {props.warning}
        </p>
      )}
      {props.error != null && (
        <span className="text-red-500 text-sm block">{props.error}</span>
      )}
    </div>
  )
})
